import { useEffect, useMemo, useRef, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import styles from "./Product.module.css";

const useOutsideClick = (ref, isOpen, onClose) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, ref]);
};

export const SearchInput = ({ value, onChange , placeholder = "Search" }) => {
  return (
    <div style={{ position: "relative", width: "100%", maxWidth: "550px" }}>
      <input
        type="text"
        className={styles.searchInput}
        placeholder={placeholder}
        style={{ paddingRight: "40px" }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span
        style={{
          position: "absolute",
          right: "16px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          color: "#A8A8A8",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <CiSearch />
      </span>
    </div>
  );
};

export const SortDropdown = ({ sortBy, sortOrder, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useOutsideClick(wrapperRef, open, () => setOpen(false));

  const handleSelect = (newSortBy, newSortOrder) => {
    onChange(newSortBy, newSortOrder);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={styles.sortWrapper}>
      <button
        className={styles.button}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        Sort By
      </button>
      {open && (
        <div className={styles.sortDropdown}>
          <div
            className={`${styles.sortOption} ${
              sortBy === "price" && sortOrder === "desc"
                ? styles.sortOptionSelected
                : ""
            }`}
            onClick={() => handleSelect("price", "desc")}
          >
            Price: High to Low
          </div>
          <div
            className={`${styles.sortOption} ${
              sortBy === "price" && sortOrder === "asc"
                ? styles.sortOptionSelected
                : ""
            }`}
            onClick={() => handleSelect("price", "asc")}
          >
            Price: Low to High
          </div>
          <div
            className={`${styles.sortOption} ${
              sortBy === "createdAt" && sortOrder === "asc"
                ? styles.sortOptionSelected
                : ""
            }`}
            onClick={() => handleSelect("createdAt", "asc")}
          >
            Oldest First
          </div>
          <div
            className={`${styles.sortOption} ${
              sortBy === "createdAt" && sortOrder === "desc"
                ? styles.sortOptionSelected
                : ""
            }`}
            onClick={() => handleSelect("createdAt", "desc")}
          >
            Newest First
          </div>
        </div>
      )}
    </div>
  );
};

export const CategoryMultiSelect = ({ value = [], onChange, categories }) => {
  const [open, setOpen] = useState(false);
  const label = useMemo(() => {
    const selectedCategories = categories
      .filter((cat) => value.map(String).includes(String(cat._id)))
      .map((cat) => cat.name);
    if (selectedCategories.length === 0) {
      return "Categories";
    }
    if (selectedCategories.length === 1) {
      return selectedCategories[0];
    }
    return `${selectedCategories?.[0]} +${selectedCategories.length - 1} more`;
  }, [value, categories]);
  const wrapperRef = useRef(null);

  const handleToggleCategory = (id) => {
    const idStr = String(id);
    const current = value.map(String);
    if (current.includes(idStr)) {
      onChange(current.filter((c) => c !== idStr));
    } else {
      onChange([...current, idStr]);
    }
  };

  useOutsideClick(wrapperRef, open, () => setOpen(false));

  return (
    <div ref={wrapperRef} className={styles.categoryMultiSelect}>
      <button
        type="button"
        className={styles.button}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          padding: "5px 20px",
          borderRadius: "16px",
          alignItems: "center",
          display: "flex",
          gap: "5px",
        }}
      >
        {label}
        {open ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
      </button>
      {open && (
        <div className={styles.categoryDropdown}>
          {categories.length === 0 ? (
            <div className={styles.categoryEmpty}>No categories</div>
          ) : (
            categories.map((cat) => {
              const idStr = String(cat._id);
              const checked = value.map(String).includes(idStr);
              return (
                <label
                  key={cat._id}
                  className={styles.categoryOption}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggleCategory(cat._id)}
                  />
                  <span className={styles.categoryLabel}>{cat.name}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export const SubcategoryMultiSelect = ({
  value = [],
  onChange,
  subCategories,
}) => {
  const [open, setOpen] = useState(false);
  const label = useMemo(() => {
    const selectedSubcategories = subCategories
      .filter((sub) => value.map(String).includes(String(sub._id)))
      .map((sub) => sub.name);
    if (selectedSubcategories.length === 0) {
      return "Subcategories";
    }
    return `${selectedSubcategories?.[0]} +${
      selectedSubcategories.length - 1
    } more`;
  }, [value, subCategories]);
  const wrapperRef = useRef(null);

  const handleToggleSubcategory = (id) => {
    const idStr = String(id);
    const current = value.map(String);
    if (current.includes(idStr)) {
      onChange(current.filter((c) => c !== idStr));
    } else {
      onChange([...current, idStr]);
    }
  };

  useOutsideClick(wrapperRef, open, () => setOpen(false));

  return (
    <div ref={wrapperRef} className={styles.subcategoryMultiSelect}>
      <button
        type="button"
        className={styles.button}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          padding: "5px 20px",
          borderRadius: "16px",
          alignItems: "center",
          display: "flex",
          gap: "5px",
        }}
      >
        {label}
        {open ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
      </button>
      {open && (
        <div className={styles.subcategoryDropdown}>
          {subCategories.length === 0 ? (
            <div className={styles.subcategoryEmpty}>No subcategories</div>
          ) : (
            subCategories.map((sub) => {
              const idStr = String(sub._id);
              const checked = value.map(String).includes(idStr);
              return (
                <label key={sub._id} className={styles.subcategoryOption}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggleSubcategory(sub._id)}
                  />
                  <span className={styles.subcategoryLabel}>{sub.name}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
export const PriceSlider = ({ value = [0, 100000], onChange }) => {
  const [open, setOpen] = useState(false);
  const [localRange, setLocalRange] = useState([value[0], value[1]]);
  const wrapperRef = useRef(null);
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(null);

  const MIN = 0;
  const MAX = 100000;

  useEffect(() => {
    setLocalRange([value[0], value[1]]);
  }, [value]);

  useOutsideClick(wrapperRef, open, () => setOpen(false));

  const handleMinChange = (e) => {
    const val = parseInt(e.target.value) || 0;
    const newMin = Math.max(MIN, Math.min(val, localRange[1]));
    setLocalRange([newMin, localRange[1]]);
  };

  const handleMaxChange = (e) => {
    const val = parseInt(e.target.value) || 0;
    const newMax = Math.max(localRange[0], Math.min(val, MAX));
    setLocalRange([localRange[0], newMax]);
  };

  const handleSave = () => {
    onChange(localRange);
    setOpen(false);
  };

  const handleCancel = () => {
    setLocalRange([value[0], value[1]]);
    setOpen(false);
  };

  const handleMouseDown = (thumb) => (e) => {
    e.preventDefault();
    setDragging(thumb);
  };

  useEffect(() => {
    if (!dragging || !trackRef.current) return;

    const handleMouseMove = (e) => {
      const track = trackRef.current;
      const rect = track.getBoundingClientRect();
      const percent = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width)
      );
      const newVal = Math.round(MIN + percent * (MAX - MIN));

      if (dragging === "min") {
        setLocalRange([Math.min(newVal, localRange[1]), localRange[1]]);
      } else {
        setLocalRange([localRange[0], Math.max(newVal, localRange[0])]);
      }
    };

    const handleMouseUp = () => {
      setDragging(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, localRange]);

  const minPercent = ((localRange[0] - MIN) / (MAX - MIN)) * 100;
  const maxPercent = ((localRange[1] - MIN) / (MAX - MIN)) * 100;

  return (
    <div ref={wrapperRef} className={styles.subcategoryMultiSelect}>
      <button
        type="button"
        className={styles.button}
        style={{
          padding: "5px 20px",
          borderRadius: "16px",
          alignItems: "center",
          display: "flex",
          gap: "5px",
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        {`₹${value[0]} - ₹${value[1]}`}
        {open ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
      </button>
      {open && (
        <div className={styles.subcategoryDropdown}>
          <div className={styles.priceContent}>
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <label className={styles.label}>Min Price</label>
                <input
                  type="number"
                  className={styles.input}
                  value={localRange[0]}
                  onChange={handleMinChange}
                  min={MIN}
                  max={localRange[1]}
                />
              </div>
              <div className={styles.inputWrapper}>
                <label className={styles.label}>Max Price</label>
                <input
                  type="number"
                  className={styles.input}
                  value={localRange[1]}
                  onChange={handleMaxChange}
                  min={localRange[0]}
                  max={MAX}
                />
              </div>
            </div>

            <div className={styles.sliderContainer}>
              <div ref={trackRef} className={styles.sliderTrack}>
                <div
                  className={styles.sliderRange}
                  style={{
                    left: `${minPercent}%`,
                    width: `${maxPercent - minPercent}%`,
                  }}
                />
                <div
                  className={styles.sliderThumb}
                  style={{ left: `${minPercent}%` }}
                  onMouseDown={handleMouseDown("min")}
                />
                <div
                  className={styles.sliderThumb}
                  style={{ left: `${maxPercent}%` }}
                  onMouseDown={handleMouseDown("max")}
                />
              </div>
            </div>

            <div className={styles.buttonContainer}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
